'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AppLogo from '@/components/ui/AppLogo';
import { CheckCircle, Shield, Users, FileText } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export default function AuthScreen() {
  const [tab, setTab] = useState<'login' | 'register'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-2/5 gradient-navy flex-col justify-between p-10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 70%, #c8a951 0%, transparent 55%)`,
          }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={40} />
            <div>
              <div className="text-white font-700 text-sm leading-tight" style={{ fontWeight: 700 }}>
                MM Shah &amp; MV Shah Academy
              </div>
              <div className="text-white/50 text-xs">Always Progressing</div>
            </div>
          </div>

          <h2 className="text-3xl font-700 text-white leading-tight mb-4" style={{ fontWeight: 700 }}>
            Employment<br />Application Portal
          </h2>
          <p className="text-white/65 text-sm leading-relaxed mb-10">
            Submit and manage your employment application securely. Our HR team reviews every
            application submitted through this portal.
          </p>

          <div className="space-y-4">
            {[
              { id: 'feat-secure', icon: Shield, text: 'Secure and confidential application process' },
              { id: 'feat-track', icon: FileText, text: 'Track your application status in real time' },
              { id: 'feat-ref', icon: CheckCircle, text: 'Receive a unique application reference number' },
              { id: 'feat-team', icon: Users, text: 'Join a dedicated school community' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="flex items-center gap-3 text-white/70 text-sm">
                  <Icon size={16} style={{ color: '#c8a951' }} />
                  {item.text}
                </div>
              );
            })}
          </div>
        </div>

        <div className="relative z-10">
          <div className="border-t border-white/15 pt-6">
            <p className="text-xs text-white/40">
              © 2026 MM Shah &amp; MV Shah Academy. This portal is for employment applications only.
            </p>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 bg-white min-h-screen overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8">
            <AppLogo size={36} />
            <div>
              <div className="text-primary font-700 text-sm leading-tight" style={{ fontWeight: 700, color: 'var(--primary)' }}>
                MM Shah &amp; MV Shah Academy
              </div>
              <div className="text-muted-foreground text-xs">Always Progressing</div>
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-secondary rounded-lg p-1 mb-7" style={{ backgroundColor: 'var(--secondary)' }}>
            {(['login', 'register'] as const).map((t) => (
              <button
                key={`auth-tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 text-sm font-600 rounded-md transition-all duration-150 ${
                  tab === t
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                style={tab === t ? { color: 'var(--primary)' } : {}}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {tab === 'login' ? (
            <LoginForm onSwitch={() => setTab('register')} />
          ) : (
            <RegisterForm onSwitch={() => setTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}